import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Tooltip from '../common/Tooltip';

const { FiTarget, FiZap, FiActivity, FiDroplet, FiSettings, FiToggleLeft, FiToggleRight, FiAlertCircle, FiInfo } = FiIcons;

const MacroTargets = ({ targets, setTargets, thresholds, setThresholds, disabled, language = 'en' }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [macroCalorieDiscrepancy, setMacroCalorieDiscrepancy] = useState(null);

  useEffect(() => {
    setShowAdvanced(thresholds.advancedMode);
  }, [thresholds.advancedMode]);

  const handleTargetChange = (macro, value) => {
    setTargets(prev => ({ ...prev, [macro]: value }));
  };

  const handleThresholdChange = (macro, value) => {
    setThresholds(prev => ({ ...prev, [macro]: parseFloat(value) || 0 }));
  };

  const toggleAdvancedMode = () => {
    const newAdvancedMode = !showAdvanced;
    setShowAdvanced(newAdvancedMode);
    if (newAdvancedMode) {
      setThresholds(prev => ({ ...prev, protein: 15, carbs: 15, fat: 15, advancedMode: true }));
    } else {
      setThresholds(prev => ({ calories: prev.calories, protein: 0, carbs: 0, fat: 0, advancedMode: false }));
    }
  };

  useEffect(() => {
    if (!showAdvanced) {
      setMacroCalorieDiscrepancy(null);
      return;
    }
    const macroCalories = calculateMacroCalories();
    const targetCalories = parseFloat(targets.calories) || 0;
    if (targetCalories > 0 && macroCalories > 0) {
      const discrepancyPercentage = Math.abs((targetCalories - macroCalories) / targetCalories) * 100;
      if (discrepancyPercentage > 5) {
        setMacroCalorieDiscrepancy({
          targetCalories,
          macroCalories,
          difference: targetCalories - macroCalories,
          percentageDiff: Math.round(discrepancyPercentage)
        });
      } else {
        setMacroCalorieDiscrepancy(null);
      }
    } else {
      setMacroCalorieDiscrepancy(null);
    }
  }, [targets.calories, targets.protein, targets.carbs, targets.fat, showAdvanced]);

  const macroFields = [
    { key: 'calories', label: 'Calories', icon: FiZap, color: 'text-orange-500', bgColor: 'bg-orange-50', borderColor: 'border-orange-200 focus:border-orange-500', unit: 'kcal', placeholder: '2000' },
    { key: 'protein', label: 'Protein', icon: FiActivity, color: 'text-red-500', bgColor: 'bg-red-50', borderColor: 'border-red-200 focus:border-red-500', unit: 'g', placeholder: '150' },
    { key: 'carbs', label: 'Carbohydrates', icon: FiTarget, color: 'text-yellow-500', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200 focus:border-yellow-500', unit: 'g', placeholder: '200' },
    { key: 'fat', label: 'Fat', icon: FiDroplet, color: 'text-green-500', bgColor: 'bg-green-50', borderColor: 'border-green-200 focus:border-green-500', unit: 'g', placeholder: '80' }
  ];

  const calculateMacroCalories = () => {
    const protein = parseFloat(targets.protein) || 0;
    const carbs = parseFloat(targets.carbs) || 0;
    const fat = parseFloat(targets.fat) || 0;
    return (protein * 4) + (carbs * 4) + (fat * 9);
  };

  const macroCalories = calculateMacroCalories();
  const targetCalories = parseFloat(targets.calories) || 0;
  const caloriesDifference = targetCalories - macroCalories;

  return (
    <motion.div id="macro-targets-panel" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`bg-white rounded-2xl shadow-lg p-6 transition-opacity duration-300 ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiTarget} className="text-2xl text-indigo-600" />
          <h2 className="text-2xl font-bold text-gray-800">Macro Targets</h2>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip text="Advanced mode lets you set specific targets and thresholds for protein, carbs, and fat for more precise optimization.">
            <SafeIcon icon={FiInfo} className="text-gray-400 cursor-pointer" />
          </Tooltip>
          <span className="text-sm text-gray-600">Advanced</span>
          <button id="advanced-mode-toggle" onClick={toggleAdvancedMode} disabled={disabled} className="text-indigo-600 hover:text-indigo-700 transition-colors disabled:opacity-50">
            <SafeIcon icon={showAdvanced ? FiToggleRight : FiToggleLeft} className="text-2xl" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <SafeIcon icon={macroFields[0].icon} className={`${macroFields[0].color} text-lg`} /> {macroFields[0].label}
          </label>
          <div className="relative">
            <input type="number" value={targets.calories} onChange={(e) => handleTargetChange('calories', e.target.value)} placeholder={macroFields[0].placeholder} disabled={disabled} className={`w-full px-4 py-3 pr-12 border-2 rounded-lg transition-all duration-200 ${macroFields[0].bgColor} ${macroFields[0].borderColor} focus:ring-2 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:bg-gray-100`} />
            <span className="absolute right-3 top-3 text-gray-500 text-sm font-medium">{macroFields[0].unit}</span>
          </div>
        </div>
        <AnimatePresence>
          {showAdvanced && macroFields.slice(1).map((field) => (
            <motion.div key={field.key} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <SafeIcon icon={field.icon} className={`${field.color} text-lg`} /> {field.label}
              </label>
              <div className="relative">
                <input type="number" value={targets[field.key]} onChange={(e) => handleTargetChange(field.key, e.target.value)} placeholder={field.placeholder} disabled={disabled} className={`w-full px-4 py-3 pr-12 border-2 rounded-lg transition-all duration-200 ${field.bgColor} ${field.borderColor} focus:ring-2 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:bg-gray-100`} />
                <span className="absolute right-3 top-3 text-gray-500 text-sm font-medium">{field.unit}</span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        <AnimatePresence>
          {macroCalorieDiscrepancy && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="sm:col-span-2 bg-orange-50 border-2 border-orange-200 rounded-lg p-4 flex gap-3">
              <SafeIcon icon={FiAlertCircle} className="text-orange-500 flex-shrink-0 text-xl mt-1" />
              <div>
                <h4 className="font-medium text-orange-800 mb-1">Macro-Calorie Mismatch</h4>
                <p className="text-sm text-orange-700">
                  Your macros calculate to <strong>{Math.round(macroCalorieDiscrepancy.macroCalories)} kcal</strong>, which is {macroCalorieDiscrepancy.difference > 0 ? <strong>{Math.abs(Math.round(macroCalorieDiscrepancy.difference))} kcal less</strong> : <strong>{Math.abs(Math.round(macroCalorieDiscrepancy.difference))} kcal more</strong>} than your target of <strong>{macroCalorieDiscrepancy.targetCalories} kcal</strong> ({macroCalorieDiscrepancy.percentageDiff}% difference).
                </p>
                <p className="text-xs text-orange-700 mt-2">Remember: 1g protein=4 kcal, 1g carbs=4 kcal, 1g fat=9 kcal</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <SafeIcon icon={FiSettings} className="text-lg text-gray-600" />
          <h3 className="font-semibold text-gray-800">{showAdvanced ? 'Advanced Thresholds' : 'Calorie Threshold'}</h3>
        </div>
        {showAdvanced ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {macroFields.map((field) => (
              <div key={`${field.key}-threshold`} className="space-y-1">
                <label className="text-xs font-medium text-gray-600">{field.label} ±%</label>
                <div className="relative">
                  <input type="number" value={thresholds[field.key]} onChange={(e) => handleThresholdChange(field.key, e.target.value)} placeholder="15" disabled={disabled} min="0" max="50" step="0.5" className="w-full px-3 py-2 pr-8 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:cursor-not-allowed disabled:bg-gray-100" />
                  <span className="absolute right-2 top-2 text-gray-400 text-xs">%</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="max-w-xs">
            <label className="text-sm font-medium text-gray-600 mb-2 block">Calorie Threshold (±%)</label>
            <div className="relative">
              <input type="number" value={thresholds.calories} onChange={(e) => handleThresholdChange('calories', e.target.value)} placeholder="10" disabled={disabled} min="0" max="50" step="0.5" className="w-full px-4 py-3 pr-12 border-2 border-orange-200 rounded-lg bg-orange-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:cursor-not-allowed disabled:bg-gray-100" />
              <span className="absolute right-3 top-3 text-gray-500 text-sm font-medium">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Only calories will be optimized within this threshold</p>
          </div>
        )}
      </div>
      <AnimatePresence>
        {showAdvanced && (targets.protein || targets.carbs || targets.fat) && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-gray-800">Macro Breakdown</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="text-red-600 font-semibold">{targets.protein || 0}g</div>
                <div className="text-gray-600">Protein</div>
                <div className="text-xs text-gray-500">{(parseFloat(targets.protein) || 0) * 4} kcal</div>
              </div>
              <div className="text-center">
                <div className="text-yellow-600 font-semibold">{targets.carbs || 0}g</div>
                <div className="text-gray-600">Carbs</div>
                <div className="text-xs text-gray-500">{(parseFloat(targets.carbs) || 0) * 4} kcal</div>
              </div>
              <div className="text-center">
                <div className="text-green-600 font-semibold">{targets.fat || 0}g</div>
                <div className="text-gray-600">Fat</div>
                <div className="text-xs text-gray-500">{(parseFloat(targets.fat) || 0) * 9} kcal</div>
              </div>
            </div>
            {macroCalories > 0 && (
              <div className="pt-3 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total from macros:</span>
                  <span className="font-semibold">{Math.round(macroCalories)} kcal</span>
                </div>
                {targetCalories > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Target calories:</span>
                    <span className="font-semibold">{targetCalories} kcal</span>
                  </div>
                )}
                {targetCalories > 0 && Math.abs(caloriesDifference) > 10 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Difference:</span>
                    <span className={`font-semibold ${caloriesDifference > 0 ? 'text-orange-600' : 'text-blue-600'}`}>
                      {caloriesDifference > 0 ? '+' : ''}{Math.round(caloriesDifference)} kcal
                    </span>
                  </div>
                )}
                <div className="mt-2 p-2 bg-gray-100 rounded-lg text-xs text-gray-600">
                  <div className="flex items-center gap-1 mb-1">
                    <SafeIcon icon={FiInfo} className="text-indigo-500" />
                    <span className="font-medium">Calorie Calculation:</span>
                  </div>
                  <div>Protein: {targets.protein || 0}g × 4={(parseFloat(targets.protein) || 0) * 4} kcal</div>
                  <div>Carbs: {targets.carbs || 0}g × 4={(parseFloat(targets.carbs) || 0) * 4} kcal</div>
                  <div>Fat: {targets.fat || 0}g × 9={(parseFloat(targets.fat) || 0) * 9} kcal</div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      {disabled && (
        <div className="text-center text-gray-500 text-sm mt-4">
          Please select foods first to set your macro targets
        </div>
      )}
    </motion.div>
  );
};

export default MacroTargets;